// ============================================================
// TEARING'S HAMMER TRIPLET (ruled 2026-10-23, the Squad Mode canvas, board
// 11): "impact, kinetic energy - three shots close to each other, high
// energy deployed in an instant". In every wound Tearing lands, three cores
// in a tight triangle, 70 ms apart, the last the hardest; the struck PART
// jolts with each (the plate allows one whole-figure move per attack, and a
// motion special may own it).
//
// Pure. Zero React dependency. Frame units; seconds.
// ============================================================

import type { Point } from './frame';
import { SHAPE_LEAD, type SpecialMoment } from './specials';

/** The triangle at rest, round its centre (frame units): two side by side, the third below. */
const SHAPE: readonly Point[] = [
  [-90, -60],
  [82, -22],
  [-8, 92],
];

/** The triplet's beat (s between cores) and how far its farthest core sits from the struck place (frame units). */
export const TRIPLET = { gap: 0.07, spread: Math.max(...SHAPE.map(([x, y]) => Math.hypot(x, y))) };

/** How hot each core lands: the third knocks hardest. */
const HEAT = [1, 1, 1.15];

export interface TripletCore {
  at: Point;
  /** Seconds after the triplet starts. */
  delay: number;
  /** Its size and knock, 1 for an ordinary core. */
  heat: number;
}

/**
 * The three cores round the struck place, the triangle turned by the hit's seed (so a burst never stacks two
 * exactly) and scaled with the plate (`k`).
 */
export function tripletOf([x, y]: Point, k: number, r: (salt: string) => number): TripletCore[] {
  const turn = r('triplet') * Math.PI * 2;
  const [cos, sin] = [Math.cos(turn), Math.sin(turn)];
  return SHAPE.map(([dx, dy], i) => ({
    at: [x + (dx * cos - dy * sin) * k, y + (dx * sin + dy * cos) * k],
    delay: Math.round(i * TRIPLET.gap * 100) / 100,
    heat: HEAT[i],
  }));
}

/** Equal within a rounding of the burst's timings. */
const near = (a: number, b: number) => Math.abs(a - b) < 1e-6;

/**
 * When the struck part's jolt starts, in seconds after the hit it rides: THIS hit's Tearing moment on that location -
 * at the hit itself, or after a shape's lead-in (engine/plate/specials). Matched exactly, never "the first within a
 * window": a burst's hits are 0.15 s apart, closer than the lead-in, so a window would take the previous hit's.
 * Null with no Tearing there.
 */
export function tripletJolt(moments: readonly SpecialMoment[], location: string, hitAt: number): number | null {
  const tear = moments.find(
    (m) =>
      m.key === 'tearing' && m.location === location && (near(m.at, hitAt) || near(m.at, hitAt + SHAPE_LEAD)),
  );
  return tear ? tear.at - hitAt : null;
}
